import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function Background3D() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog("#030303", 8, 22);

    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 100);
    camera.position.set(0, 0, 12);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Particles
    const COUNT = 2200;
    const positions = new Float32Array(COUNT * 3);
    const originals = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      const r = 6 + Math.random() * 4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      originals[i * 3] = x;
      originals[i * 3 + 1] = y;
      originals[i * 3 + 2] = z;
    }
    const pointsGeom = new THREE.BufferGeometry();
    pointsGeom.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    const pointsMat = new THREE.PointsMaterial({
      size: 0.025,
      color: 0xffffff,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
      depthWrite: false,
    });
    const points = new THREE.Points(pointsGeom, pointsMat);
    scene.add(points);

    // Faint connecting lines
    const SEGS = 70;
    const linePos = new Float32Array(SEGS * 2 * 3);
    for (let i = 0; i < SEGS; i++) {
      const a = Math.floor(Math.random() * COUNT);
      const b = Math.floor(Math.random() * COUNT);
      linePos[i * 6] = originals[a * 3];
      linePos[i * 6 + 1] = originals[a * 3 + 1];
      linePos[i * 6 + 2] = originals[a * 3 + 2];
      linePos[i * 6 + 3] = originals[b * 3];
      linePos[i * 6 + 4] = originals[b * 3 + 1];
      linePos[i * 6 + 5] = originals[b * 3 + 2];
    }
    const lineGeom = new THREE.BufferGeometry();
    lineGeom.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
    const lineMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.05,
      depthWrite: false,
    });
    const lines = new THREE.LineSegments(lineGeom, lineMat);
    scene.add(lines);

    // Mouse + state
    const pointer = { x: 0, y: 0 };
    const smooth = { x: 0, y: 0 };
    const onMove = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove);

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    const clock = new THREE.Clock();
    let raf;
    const tick = () => {
      const t = clock.getElapsedTime();
      smooth.x += (pointer.x - smooth.x) * 0.04;
      smooth.y += (pointer.y - smooth.y) * 0.04;

      const arr = pointsGeom.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        const ix = i * 3;
        const ox = originals[ix];
        const oy = originals[ix + 1];
        const oz = originals[ix + 2];
        const wave =
          Math.sin(t * 0.4 + ox * 0.4) * 0.08 +
          Math.cos(t * 0.3 + oy * 0.3) * 0.08;
        arr[ix] = ox + wave + smooth.x * 0.4;
        arr[ix + 1] = oy + wave + smooth.y * 0.4;
        arr[ix + 2] = oz + wave;
      }
      pointsGeom.attributes.position.needsUpdate = true;

      points.rotation.y = t * 0.04 + smooth.x * 0.15;
      points.rotation.x = smooth.y * 0.1;
      lines.rotation.y = t * 0.04 + smooth.x * 0.15;
      lines.rotation.x = smooth.y * 0.1;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      pointsGeom.dispose();
      pointsMat.dispose();
      lineGeom.dispose();
      lineMat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-0 pointer-events-none"
      data-testid="background-3d"
    >
      <div ref={containerRef} className="absolute inset-0" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(212,255,0,0.05), transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(255,255,255,0.03), transparent 60%)",
        }}
      />
    </div>
  );
}
